import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import { useGetWorkerShifts } from "../../WorkerShifts/api/useGetWorkerShifts";
import { type OpenShiftWorkplace } from "../Open/types";
import {
  applyPreShiftOrientationLeadTime,
  evaluatePreShiftOrientationBase,
  type PreShiftOrientationBaseEvaluation,
  type PreShiftOrientationEvaluation,
} from "./hasPreShiftOrientation";
import { useWorkplacesWithOrientationCredentials } from "./useWorkplacesWithOrientationCredentials";
import { useWorkplacesWithUpcomingPreShiftOrientation } from "./useWorkplacesWithUpcomingPreShiftOrientation";

/**
 * Evaluates pre-shift orientation for all shifts in a list, returning a map
 * keyed by shift ID.
 */
export function usePreShiftOrientationsByShiftId(
  shifts: OpenShift[],
  workplacesMap: Map<string, OpenShiftWorkplace>
): Map<string, PreShiftOrientationEvaluation> {
  const worker = useDefinedWorker();

  const { workplacesWithOrientationCredentials, isLoading: isLoadingCredentials } =
    useWorkplacesWithOrientationCredentials();
  const { workplacesWithUpcomingPreShiftOrientation, isLoading: isLoadingActiveOrientations } =
    useWorkplacesWithUpcomingPreShiftOrientation();
  const { shifts: workerShifts, isLoading: isLoadingBookedShifts } = useGetWorkerShifts({ worker });

  const bookedShifts = useMemo(
    () =>
      workerShifts.map((workerShift) => ({
        start: workerShift.start,
        workplaceId: workerShift.facility.userId ?? "",
      })),
    [workerShifts]
  );

  const isLoading = isLoadingCredentials || isLoadingActiveOrientations || isLoadingBookedShifts;

  // Memoize the time-independent filtering work so it only re-runs when the
  // underlying data changes. The lead-time check, applied below, re-checks
  // `new Date()` every render so the transition into the no-orientation
  // fallback stays responsive without waiting on a dep change.
  const baseEvaluationsByShiftId = useMemo(() => {
    if (isLoading) {
      return new Map<string, PreShiftOrientationBaseEvaluation>();
    }

    const map = new Map<string, PreShiftOrientationBaseEvaluation>();

    for (const shift of shifts) {
      const workplaceId = shift.relationships.workplace.data.id;
      const workplace = workplacesMap.get(workplaceId);

      map.set(
        shift.id,
        evaluatePreShiftOrientationBase({
          workplaceId,
          shiftStart: shift.attributes.start,
          preShiftOrientationLengthInMinutes:
            workplace?.attributes.preShiftOrientationLengthInMinutes,
          workplacesWithOrientationCredentials,
          workplacesWithUpcomingPreShiftOrientation,
          bookedShifts,
        })
      );
    }

    return map;
  }, [
    isLoading,
    shifts,
    workplacesMap,
    workplacesWithOrientationCredentials,
    workplacesWithUpcomingPreShiftOrientation,
    bookedShifts,
  ]);

  const now = new Date();
  const evaluationsByShiftId = new Map<string, PreShiftOrientationEvaluation>();
  for (const [shiftId, base] of baseEvaluationsByShiftId) {
    evaluationsByShiftId.set(shiftId, applyPreShiftOrientationLeadTime(base, now));
  }

  return evaluationsByShiftId;
}
